<template>
  <vs-popup id="popupAISafetyMeasures" title="AI 안전보건조치 추천" :active.sync="showPopup" :width="popupWidth">
    <div>
      <!-- 로딩 상태 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <div class="pulse-animation">
            <vs-loading type="circles" color="primary"></vs-loading>
          </div>
          <div class="loading-dots">
            <span class="dot dot1"></span>
            <span class="dot dot2"></span>
            <span class="dot dot3"></span>
          </div>
        </div>
        <p class="loading-text animated-text">AI가 안전보건조치를 분석 중입니다...</p>
        <p class="loading-subtext">잠시만 기다려주세요 (약 10초 소요)</p>
        <div class="progress-bar">
          <div class="progress-fill"></div>
          <div class="progress-dots">
            <div class="progress-dot"></div>
            <div class="progress-dot"></div>
            <div class="progress-dot"></div>
            <div class="progress-dot"></div>
          </div>
        </div>
      </div>

      <!-- 응답 데이터 표시 -->
      <div v-else-if="recommendationData && recommendationData.length > 0" class="recommendation-container">
        <!-- PC 레이아웃 (768px 이상) -->
        <div class="desktop-layout" v-show="!isMobile">
          <!-- 탭 헤더 -->
          <vs-tabs color="primary" class="recommendation-tabs">
            <vs-tab label="안전보건조치">
              <div class="tab-content">
                <div class="selection-info mb-4">
                  <p class="text-sm text-gray-600">
                    <feather-icon icon="InfoIcon" size="16" class="mr-1" />
                    원하는 안전보건조치를 선택하세요. 선택된 항목은 현재 안전보건조치 필드에 추가됩니다.
                  </p>
                  <p class="text-sm text-blue-600 mt-1">선택된 항목: {{ selectedItems.length }}개</p>
                </div>

                <div class="recommendation-list">
                  <div v-for="(item, index) in recommendationData" :key="`safety-${index}`" class="recommendation-item" :class="{ selected: selectedItems.includes(index) }" @click="toggleSelection(index)">
                    <div class="item-checkbox" @click.stop>
                      <vs-checkbox :value="selectedItems.includes(index)" @input="handleCheckboxClick(index)" />
                    </div>
                    <div class="item-content">
                      <div class="item-number">{{ index + 1 }}</div>
                      <div class="item-text">{{ item }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </vs-tab>
          </vs-tabs>
        </div>

        <!-- 모바일 레이아웃 (768px 미만) -->
        <div class="mobile-layout" v-show="isMobile">
          <!-- 선택 정보 헤더 (sticky) -->
          <div class="mobile-header mobile-sticky-header">
            <div class="header-title">
              <feather-icon icon="ShieldIcon" size="18" class="mr-2" />
              <span>AI 안전보건조치 추천</span>
            </div>
            <div class="selection-count">{{ selectedItems.length }}개 선택됨</div>
          </div>

          <!-- 스크롤 가능한 컨텐츠 영역 -->
          <div class="mobile-content-wrapper">
            <!-- 안내 메시지 -->
            <div class="mobile-info">
              <p class="info-text">
                <feather-icon icon="InfoIcon" size="14" class="mr-1" />
                원하는 안전보건조치를 선택하세요
              </p>
            </div>

            <!-- 모바일 추천 리스트 -->
            <div class="mobile-recommendation-list">
              <div v-for="(item, index) in recommendationData" :key="`mobile-safety-${index}`" class="mobile-recommendation-card" :class="{ selected: selectedItems.includes(index) }" @click="toggleSelection(index)">
                <div class="card-content-only">
                  <p class="card-text">{{ item }}</p>
                  <div class="card-selection-indicator" v-if="selectedItems.includes(index)">
                    <feather-icon icon="CheckIcon" size="16" />
                  </div>
                </div>
              </div>
            </div>

            <!-- 하단 여백 (sticky 버튼을 위한 공간) -->
            <div class="mobile-bottom-spacer"></div>
          </div>
        </div>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="error" class="error-container">
        <div class="error-icon">
          <feather-icon icon="AlertTriangleIcon" size="48" class="text-red-500" />
        </div>
        <p class="error-text">안전보건조치 추천을 가져오는 중 오류가 발생했습니다.</p>
        <p class="error-subtext">{{ error }}</p>
      </div>

      <!-- 데이터가 없는 경우 -->
      <div v-else-if="!loading && !recommendationData" class="empty-container">
        <div class="empty-icon">
          <feather-icon icon="SearchIcon" size="48" class="text-gray-400" />
        </div>
        <p class="empty-text">추천할 안전보건조치가 없습니다.</p>
      </div>

      <!-- PC 하단 버튼 -->
      <div class="button-container desktop-buttons" v-show="!isMobile && ((recommendationData && recommendationData.length > 0) || error || (!loading && !recommendationData))">
        <div class="button-left">
          <vs-button color="secondary" type="border" @click="handleRecommendAgain" :disabled="loading"> 다시추천 </vs-button>
        </div>
        <div class="button-right">
          <vs-button color="secondary" type="border" @click="handleCancel"> 취소 </vs-button>
          <vs-button color="primary" @click="handleSelectComplete" :disabled="selectedItems.length === 0"> 선택완료 ({{ selectedItems.length }}) </vs-button>
        </div>
      </div>

      <!-- 모바일 하단 버튼 (sticky) -->
      <div class="mobile-button-container mobile-sticky-buttons" v-show="isMobile && ((recommendationData && recommendationData.length > 0) || error || (!loading && !recommendationData))">
        <div class="mobile-button-row">
          <vs-button color="secondary" type="border" @click="handleRecommendAgain" :disabled="loading" class="mobile-button mobile-button-secondary"> 다시추천 </vs-button>
        </div>
        <div class="mobile-button-row">
          <vs-button color="secondary" type="border" @click="handleCancel" class="mobile-button mobile-button-cancel"> 취소 </vs-button>
          <vs-button color="primary" @click="handleSelectComplete" :disabled="selectedItems.length === 0" class="mobile-button mobile-button-primary"> 선택완료 ({{ selectedItems.length }}) </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: "PopupAISafetyMeasures",
  props: {
    value: Boolean,
    requestData: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    value(data) {
      this.showPopup = data;
      if (data) {
        // 팝업이 열릴 때마다 선택 항목 초기화
        this.resetSelections();
        this.loadRecommendations();
      }
    },
    showPopup(data) {
      this.$emit("input", data);
      if (!data) {
        // 팝업이 닫힐 때도 선택 항목 초기화
        this.resetSelections();
      }
    },
    selectedItems: {
      deep: true,
      handler() {
        this.updateIsSelected();
      },
    },
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    popupWidth() {
      return this.isMobile ? "95vw" : "1000px";
    },
  },
  data() {
    return {
      showPopup: false,
      loading: false,
      recommendationData: [],
      selectedItems: [],
      isSelected: {},
      error: null,
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    // 화면 크기 변경 감지
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener("resize", this.handleResize);

    const backButtonRouteGuard = this.$router.beforeEach((to, from, next) => {
      if (this.showPopup) {
        this.showPopup = false;
        next(false);
        return;
      }
      next(true);
    });
    this.$once("hook:destroyed", () => {
      backButtonRouteGuard();
      window.removeEventListener("resize", this.handleResize);
    });
  },
  created() {
    this.showPopup = this.value;
  },
  methods: {
    resetSelections() {
      this.selectedItems = [];
      this.isSelected = {};
    },

    async loadRecommendations() {
      this.loading = true;
      this.error = null;

      try {
        const { 평가구분, 유해위험요인, 설명, 재해형태 } = this.requestData;
        const response = await this.$store.dispatch("miras/GET_CURRENT_SAFETY_MEASURES", {
          평가구분,
          유해위험요인,
          설명,
          재해형태,
        });

        if (response && response.success && response.data && response.data.현재_안전보건조치) {
          this.recommendationData = response.data.현재_안전보건조치;
          this.initializeSelection();
        } else {
          this.error = "응답 데이터 형식이 올바르지 않습니다.";
        }
      } catch (error) {
        console.error("AI 안전보건조치 추천 오류:", error);
        this.error = error.message || "서버 연결에 실패했습니다.";
      } finally {
        this.loading = false;
      }
    },

    initializeSelection() {
      this.isSelected = {};
      this.recommendationData.forEach((_, index) => {
        this.$set(this.isSelected, index, this.selectedItems.includes(index));
      });
    },

    updateIsSelected() {
      this.recommendationData.forEach((_, index) => {
        this.$set(this.isSelected, index, this.selectedItems.includes(index));
      });
    },

    handleCheckboxClick(index) {
      this.toggleSelection(index);
    },

    toggleSelection(index) {
      if (this.selectedItems.includes(index)) {
        this.selectedItems = this.selectedItems.filter((item) => item !== index);
      } else {
        this.selectedItems.push(index);
      }
      this.$set(this.isSelected, index, this.selectedItems.includes(index));
    },

    async handleRecommendAgain() {
      // 선택된 항목이 있을 때 확인 메시지 표시
      if (this.selectedItems.length > 0) {
        const message = `현재 ${this.selectedItems.length}개 항목이 선택되어 있습니다.\n다시추천을 받으면 선택이 초기화됩니다.\n계속하시겠습니까?`;
        const confirmed = confirm(message);
        if (!confirmed) {
          return;
        }
      }

      // 선택 항목 초기화 후 새로운 추천 받기
      this.resetSelections();
      await this.loadRecommendations();
    },

    handleCancel() {
      this.showPopup = false;
      this.$emit("cancel");
    },

    handleSelectComplete() {
      const selectedData = this.selectedItems.map((index) => ({
        index,
        text: this.recommendationData[index],
      }));

      this.showPopup = false;
      this.$emit("selectComplete", selectedData);
    },
  },
};
</script>

<style scoped lang="scss">
.loading-container {
  text-align: center;
  padding: 60px 20px;

  .loading-spinner {
    margin-bottom: 20px;
    position: relative;
  }

  .pulse-animation {
    animation: pulse 2s infinite;
  }

  .loading-dots {
    display: flex;
    justify-content: center;
    margin: 20px 0;

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #007bff;
      margin: 0 4px;
      animation: bounce 1.5s infinite ease-in-out;

      &.dot1 {
        animation-delay: -0.32s;
      }

      &.dot2 {
        animation-delay: -0.16s;
      }

      &.dot3 {
        animation-delay: 0s;
      }
    }
  }

  .loading-text {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;

    &.animated-text {
      animation: fadeInOut 2s infinite;
    }
  }

  .loading-subtext {
    font-size: 14px;
    color: #666;
  }

  .progress-bar {
    width: 100%;
    height: 4px;
    background: #e9ecef;
    border-radius: 2px;
    margin-top: 20px;
    overflow: hidden;
    position: relative;

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #007bff, #28a745, #007bff);
      background-size: 200% 100%;
      animation: progressSlide 1.5s infinite linear;
      width: 100%;
      border-radius: 2px;
    }

    .progress-dots {
      position: absolute;
      top: -8px;
      left: 0;
      width: 100%;
      height: 20px;
      display: flex;
      justify-content: space-around;
      align-items: center;

      .progress-dot {
        width: 6px;
        height: 6px;
        background: #007bff;
        border-radius: 50%;
        animation: dotPulse 2s infinite ease-in-out;

        &:nth-child(1) {
          animation-delay: 0s;
        }
        &:nth-child(2) {
          animation-delay: 0.5s;
        }
        &:nth-child(3) {
          animation-delay: 1s;
        }
        &:nth-child(4) {
          animation-delay: 1.5s;
        }
      }
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes progressSlide {
  0% {
    background-position: 200% 0;
    transform: translateX(-100%);
  }
  50% {
    background-position: 0% 0;
    transform: translateX(0%);
  }
  100% {
    background-position: -200% 0;
    transform: translateX(100%);
  }
}

@keyframes dotPulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1.2);
  }
}

.recommendation-container {
  min-height: 400px;

  .recommendation-tabs {
    .tab-content {
      padding: 20px 0;
    }
  }

  .selection-info {
    background: #f8f9fa;
    padding: 12px 16px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
  }

  .recommendation-list {
    max-height: 400px;
    overflow-y: auto;

    .recommendation-item {
      display: flex;
      align-items: flex-start;
      padding: 16px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      margin-bottom: 12px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #007bff;
        background-color: #f8f9fa;
      }

      &.selected {
        border-color: #007bff;
        background-color: #e7f3ff;
      }

      .item-checkbox {
        margin-right: 12px;
        margin-top: 2px;
      }

      .item-content {
        display: flex;
        flex: 1;

        .item-number {
          width: 24px;
          height: 24px;
          background: #007bff;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 500;
          margin-right: 12px;
          flex-shrink: 0;
        }

        .item-text {
          flex: 1;
          line-height: 1.6;
          font-size: 14px;
          color: #333;
        }
      }
    }
  }
}

.error-container,
.empty-container {
  text-align: center;
  padding: 60px 20px;

  .error-icon,
  .empty-icon {
    margin-bottom: 20px;
  }

  .error-text,
  .empty-text {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }

  .error-subtext {
    font-size: 14px;
    color: #666;
  }
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 20px;

  .button-left {
    // 왼쪽 정렬
  }

  .button-right {
    display: flex;
    gap: 8px;
  }
}

// 모바일 레이아웃 스타일
.mobile-layout {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .mobile-sticky-header {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .header-title {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }

    .selection-count {
      background: #007bff;
      color: white;
      padding: 4px 12px;
      border-radius: 16px;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .mobile-content-wrapper {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 10px;
  }

  .mobile-info {
    margin-bottom: 12px;

    .info-text {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #666;
      margin: 0;
    }
  }

  .mobile-recommendation-list {
    .mobile-recommendation-card {
      background: white;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      margin-bottom: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      outline: none;
      -webkit-tap-highlight-color: transparent;

      // 터치 디바이스에서는 hover 효과 비활성화
      @media (hover: hover) and (pointer: fine) {
        &:hover:not(.selected) {
          border-color: #007bff;
          box-shadow: 0 2px 6px rgba(0, 123, 255, 0.1);
        }
      }

      // 모바일에서 터치 후 포커스 상태 제거
      &:focus {
        outline: none;
      }

      &:active {
        transform: scale(0.98);
      }

      &.selected {
        border-color: #007bff;
        border-width: 3px;
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
      }

      .card-content-only {
        padding: 12px 16px;
        position: relative;

        .card-text {
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          margin: 0;
          word-break: break-word;
        }

        .card-selection-indicator {
          position: absolute;
          bottom: 5px;
          right: 8px;
          color: #007bff;
          border-radius: 50%;
          width: 8px;
          height: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }

  .mobile-bottom-spacer {
    height: 120px;
  }
}

.mobile-button-container {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;

  &.mobile-sticky-buttons {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: #f8f9fa;
    margin-top: 0;
    padding: 12px;
    border-top: 1px solid #e9ecef;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  }

  .mobile-button-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .mobile-button {
      flex: 1;
      min-height: 44px;
      font-size: 15px;
      font-weight: 500;
      border-radius: 6px;
      transition: all 0.3s ease;

      &.mobile-button-secondary {
        width: 100%;
      }

      &.mobile-button-cancel {
        flex: 0.4;
      }

      &.mobile-button-primary {
        flex: 0.6;
        font-weight: 600;
      }
    }
  }
}

// 모바일 환경에서 팝업 스타일 조정
@media (max-width: 767px) {
  #popupAISafetyMeasures::v-deep {
    .vs-popup {
      .vs-popup--content {
        margin: 10px;
        max-height: 85vh;
        overflow-y: auto;
        padding: 10px !important;
      }
      .vs-popup--content {
        width: calc(100% - 20px);
      }

      .vs-popup--header {
        padding: 16px 20px 12px;

        .vs-popup--title {
          font-size: 18px;
          font-weight: 600;
        }
      }
    }
  }

  .recommendation-container {
    &.mobile-layout {
      padding: 0;
    }
  }

  .mobile-button-container {
    margin-left: -10px;
    margin-right: -10px;
    margin-bottom: -10px;
    padding: 10px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;

    &.mobile-sticky-buttons {
      margin-left: -10px;
      margin-right: -10px;
      margin-bottom: -10px;
      padding: 12px;
      position: sticky;
      bottom: -10px;
      z-index: 10;
      background: #f8f9fa;
      border-top: 1px solid #e9ecef;
      box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  .loading-container {
    padding: 40px 20px;

    .loading-text {
      font-size: 15px;
    }

    .loading-subtext {
      font-size: 13px;
    }
  }

  .error-container,
  .empty-container {
    padding: 40px 20px;

    .error-text,
    .empty-text {
      font-size: 15px;
    }

    .error-subtext {
      font-size: 13px;
    }
  }
}

// PC 환경에서 기존 스타일 유지
@media (min-width: 768px) {
  #popupAISafetyMeasures::v-deep {
    .vs-popup {
      min-width: 1000px;
      .vs-popup--content {
        max-height: 90vh;
        overflow-y: auto;
      }
    }
  }
}
</style>
